import { addDays, addMonths } from '@/utils/dates';

export enum TimeUnit {
  DAYS = "days",
  WEEKS = "weeks",
  MONTHS = "months",
  YEARS = "years",
}

export const timeUnitOptions = Object.values(TimeUnit);

export const TimeUnitLabels: Record<TimeUnit, string> = {
  [TimeUnit.DAYS]: 'Dias',
  [TimeUnit.WEEKS]: 'Semanas',
  [TimeUnit.MONTHS]: 'Meses',
  [TimeUnit.YEARS]: 'Anos',
};

const TimeUnitSingularLabels: Record<TimeUnit, string> = {
  [TimeUnit.DAYS]: 'dia',
  [TimeUnit.WEEKS]: 'semana',
  [TimeUnit.MONTHS]: 'mês',
  [TimeUnit.YEARS]: 'ano',
};

/** "1 mês", "3 meses" — accepts TimeUnit values ("months") and the pipeline's ("MONTH"). Unknown → as-is. */
export const formatFrequency = (value: number | null | undefined, unit: string | null | undefined): string => {
  if (value === null || value === undefined || !unit) return '';
  const normalized = unit.toLowerCase().replace(/s$/, '') + 's';
  if (!Object.values(TimeUnit).includes(normalized as TimeUnit)) return `${value} ${unit}`;
  const timeUnit = normalized as TimeUnit;
  return `${value} ${value === 1 ? TimeUnitSingularLabels[timeUnit] : TimeUnitLabels[timeUnit].toLowerCase()}`;
};

/** `date` moved by `amount` of `unit` ("3 semanas depois"). */
export const addTimeUnits = (date: Date, amount: number, unit: TimeUnit): Date => {
  switch (unit) {
    case TimeUnit.DAYS: return addDays(date, amount);
    case TimeUnit.WEEKS: return addDays(date, amount * 7);
    case TimeUnit.MONTHS: return addMonths(date, amount);
    case TimeUnit.YEARS: return addMonths(date, amount * 12);
  }
};

export const timeUnitSelectOptions =timeUnitOptions.map((unit) => ({
  label: TimeUnitLabels[unit],
  value: unit,
}));

export const formatTimeAgo = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return '';
  }
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 1000) { // Less than a second
    return 'just now';
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const years = Math.floor(days / 365.25);
  const remainingDaysAfterYears = days % 365.25;
  const months = Math.floor(remainingDaysAfterYears / 30.44);

  if (years > 0) {
    const yearText = years > 1 ? 'anos' : 'ano';
    if (months > 0) {
      const monthText = months > 1 ? 'meses' : 'mês';
      return `${years} ${yearText} e ${months} ${monthText} atrás`;
    }
    return `${years} ${yearText} atrás`;
  }

  if (months > 0) {
    const monthText = months > 1 ? 'meses' : 'mês';
    return `${months} ${monthText} atrás`;
  }

  if (days > 0) {
    const dayText = days > 1 ? 'dias' : 'dia';
    return `${days} ${dayText} atrás`;
  }

  return 'hoje';
};

const parseDateValue = (value: string): Date | null => {
  const brMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (brMatch) {
    const [, day, month, year] = brMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  // Date-only values represent local calendar days, not UTC midnight. needed to make the browser avoid timezone conversion.
  const fallback = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const describeDuration = (totalDays: number): string => {
  const years = Math.floor(totalDays / 365.25);
  const months = Math.floor((totalDays - years * 365.25) / 30.44);
  const days = Math.floor(totalDays - years * 365.25 - months * 30.44);

  if (years > 0) {
    const yearText = `${years} ${years > 1 ? 'anos' : 'ano'}`;
    return months > 0 ? `${yearText} e ${months} ${months > 1 ? 'meses' : 'mês'}` : yearText;
  }
  if (months > 0) {
    const monthText = `${months} ${months > 1 ? 'meses' : 'mês'}`;
    return days > 0 ? `${monthText} e ${days} ${days > 1 ? 'dias' : 'dia'}` : monthText;
  }
  return `${days} ${days === 1 ? 'dia' : 'dias'}`;
};

/**
 * Relative time until a future date (or since a past one), for values such as
 * certificate expiration dates. Accepts Brazilian `dd/mm/yyyy` strings as well
 * as ISO dates. Returns e.g. "em 5 meses", "vence hoje", "vencida há 3 dias".
 */
export const formatTimeUntil = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return '';
  }
  const target = parseDateValue(dateString);
  if (!target) {
    return '';
  }

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTarget = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const dayMs = 1000 * 60 * 60 * 24;
  const totalDays = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / dayMs);

  if (totalDays === 0) {
    return 'vence hoje';
  }
  if (totalDays < 0) {
    return `vencida há ${describeDuration(Math.abs(totalDays))}`;
  }
  return `em ${describeDuration(totalDays)}`;
};

export const formatDuration = (start: string | Date, end: string | Date | null): string => {
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  if (years === 0 && months === 0) return '';

  const yearText = years > 0 ? `${years} year${years > 1 ? 's' : ''}` : '';
  const monthText = months > 0 ? `${months} month${months > 1 ? 's' : ''}` : '';

  return `(${[yearText, monthText].filter(Boolean).join(', ')})`;
};

/**
 * Very short elapsed time for dense table cells: "agora", "3h", "5d", "2sem", "7m", "2a".
 * Deliberately terser than `formatTimeAgo`, which spells the duration out in full.
 */
export const formatAgeShort = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';

  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes}min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.floor(days / 7)}sem`;
  if (days < 365) return `${Math.floor(days / 30.44)}m`;
  return `${Math.floor(days / 365.25)}a`;
};

/** Whole days elapsed since `dateString`; null when unparseable. */
export const daysSince = (dateString: string | null | undefined): number | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((Date.now() - date.getTime()) / 86_400_000);
};

/**
 * Parses a backend timestamp as UTC. Naive ISO datetimes (no `Z` or offset) are UTC
 * instants; `new Date()` would otherwise read them as local time.
 */
export const parseUtcTimestamp = (timestamp: string): Date =>
  new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(timestamp) ? timestamp : `${timestamp}Z`);
